/**
 * 角色管理頁面
 * 參照 UI: UI-1-5 角色管理（RBAC 矩陣）
 * 功能：角色列表 + 建立 + 編輯（含頁面權限 + 操作權限矩陣）
 */
import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useEditionStore } from '../../store/edition.store';
import { useAuthStore } from '../../store/auth.store';
import { adminVenueListOptions } from '../../hooks/adminVenueQueries';
import { apiClient } from '../../lib/api.ts';
import type {
  RoleDto,
  CreateRoleDto,
  UpdateRoleDto,
  PagePath,
  ActionKey,
  FeatureKey,
} from '@pokerroom/types';
import { ACTION_KEY_GROUPS, getConfigurablePages } from '@pokerroom/types';
import {
  ROLE_PAGE_GROUPS,
  ROLE_ACTION_GROUPS,
  orderPagePermissions,
  getRolePageGroups,
  initialPagePermissions,
  countPagePermissions,
} from './rolePermissionOrder';

/** API 回應包裝格式 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
}

// ============================================================
// 從 SIDEBAR_DEFINITION 衍生所有頁面路徑（扁平列表）
// ============================================================
/** pagePath → labelKey 對照表（供 badge 顯示用） */
function getPageLabelKeyMap(): Record<string, string> {
  const map: Record<string, string> = {};
  for (const entry of ROLE_PAGE_GROUPS) {
    if (entry.type === 'single' && entry.pagePath) {
      map[entry.pagePath] = entry.labelKey;
    } else if (entry.children) {
      for (const child of entry.children) {
        map[child.pagePath] = child.labelKey;
      }
    }
  }
  return map;
}

const PAGE_LABEL_KEY_MAP = getPageLabelKeyMap();

/** actionKey → i18n key 對照（從 ACTION_KEY_GROUPS 自動生成） */
const ACTION_KEY_I18N: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const group of ACTION_KEY_GROUPS) {
    for (const action of group.actions) {
      map[action] = `action_${action.replace(':', '_')}`;
    }
  }
  return map;
})();

/** 所有 ActionKey 扁平列表 */
const ALL_ACTION_KEYS = ACTION_KEY_GROUPS.flatMap((g) => g.actions);

// ============================================================
// Indeterminate Checkbox
// ============================================================
function IndeterminateCheckbox({
  checked,
  indeterminate,
  onChange,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="accent-primary shrink-0"
    />
  );
}

// ============================================================
// 角色 Modal（建立 / 編輯共用）
// ============================================================
interface RoleModalProps {
  role?: RoleDto; // 若有 role 表示編輯模式
  onClose: () => void;
  onSubmit: (data: CreateRoleDto | UpdateRoleDto) => void;
  isLoading: boolean;
}

function RoleModal({ role, onClose, onSubmit, isLoading }: RoleModalProps) {
  const { t } = useTranslation(['admin', 'common']);
  const isEdit = !!role;

  const enabledFeatures = useEditionStore((state) => state.enabledFeatures);
  const hasFeature = (key: FeatureKey) => enabledFeatures.includes(key);
  const isSystemUser = useAuthStore((state) => state.user?.isSystemUser);
  const pageGroups = getRolePageGroups(isSystemUser ? undefined : hasFeature);
  const editablePaths = pageGroups.flatMap((entry) =>
    entry.pagePath ? [entry.pagePath] : entry.children!.map((child) => child.pagePath),
  );
  const buildInitialPagePerms = () => initialPagePermissions(role?.pagePermissions);

  const buildInitialActionPerms = () => {
    // 先建立全部 key = false 的基底
    const base = Object.fromEntries(ALL_ACTION_KEYS.map((k) => [k, false]));
    if (role) {
      // 用角色已存的權限覆蓋
      for (const a of role.actionPermissions) {
        base[a.actionKey] = a.isAllowed;
      }
    }
    return base;
  };

  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [requireDeviceBinding, setRequireDeviceBinding] = useState(
    role?.requireDeviceBinding ?? true,
  );
  const [pagePerms, setPagePerms] = useState<Record<string, boolean>>(buildInitialPagePerms);
  const [actionPerms, setActionPerms] = useState<Record<string, boolean>>(buildInitialActionPerms);
  const [nameError, setNameError] = useState('');

  const togglePagePerm = (path: string) => {
    setPagePerms((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  const toggleActionPerm = (key: string) => {
    setActionPerms((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setNameError(t('roleNameRequired'));
      return;
    }

    const pagePermissions = Object.entries(pagePerms).map(([pagePath, isVisible]) => ({
      pagePath: pagePath as PagePath,
      isVisible,
    }));
    const actionPermissions = Object.entries(actionPerms).map(([actionKey, isAllowed]) => ({
      actionKey: actionKey as ActionKey,
      isAllowed,
    }));

    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      requireDeviceBinding,
      ...(role ? { expectedUpdatedAt: role.updatedAt } : {}),
      pagePermissions,
      actionPermissions,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-base-100 rounded-2xl border border-base-300 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-base-content mb-4">
            {isEdit ? t('editRole') : t('createRole')}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* 基本資訊 */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-base-content/70 mb-1">
                  {t('roleNameLabel')}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setNameError('');
                  }}
                  // R-06: only the SUPER_ADMIN-keyed built-in role may be renamed
                  disabled={isEdit && role?.isSystemRole && role.systemKey !== 'SUPER_ADMIN'}
                  placeholder={t('roleNamePlaceholder')}
                  className="w-full px-3 py-2 rounded-lg bg-base-200 border border-base-300 text-base-content focus:outline-hidden focus:border-primary text-sm disabled:opacity-50"
                />
                {nameError && <p className="text-red-500 text-xs mt-1">{nameError}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-base-content/70 mb-1">
                  {t('descriptionLabel')}
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('roleDescPlaceholder')}
                  className="w-full px-3 py-2 rounded-lg bg-base-200 border border-base-300 text-base-content focus:outline-hidden focus:border-primary text-sm"
                />
              </div>
            </div>

            {/* 裝置綁定 */}
            <label className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-base-200 border border-base-300 cursor-pointer hover:border-primary transition">
              <input
                type="checkbox"
                checked={requireDeviceBinding}
                onChange={(e) => setRequireDeviceBinding(e.target.checked)}
                className="accent-primary shrink-0"
              />
              <div>
                <span className="text-sm text-base-content font-medium">
                  {t('requireDeviceBinding')}
                </span>
                <p className="text-xs text-base-content/50 mt-0.5">
                  {t('requireDeviceBindingHint')}
                </p>
              </div>
            </label>

            {/* 頁面可見性（主/從分支） */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-base-content">{t('pageVisibility')}</h3>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setPagePerms((previous) => ({
                        ...previous,
                        ...Object.fromEntries(editablePaths.map((path) => [path, true])),
                      }))
                    }
                    className="text-xs text-primary hover:underline"
                  >
                    {t('selectAll')}
                  </button>
                  <span className="text-base-content/30 text-xs">|</span>
                  <button
                    type="button"
                    onClick={() =>
                      setPagePerms((previous) => ({
                        ...previous,
                        ...Object.fromEntries(editablePaths.map((path) => [path, false])),
                      }))
                    }
                    className="text-xs text-base-content/50 hover:underline"
                  >
                    {t('clearAll')}
                  </button>
                </div>
              </div>
              <div className="space-y-1">
                {pageGroups.map((entry) => {
                  if (entry.type === 'single' && entry.pagePath) {
                    return (
                      <label
                        key={entry.pagePath}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-base-200 border border-base-300 cursor-pointer hover:border-primary transition text-sm"
                      >
                        <input
                          type="checkbox"
                          checked={pagePerms[entry.pagePath] ?? false}
                          onChange={() => togglePagePerm(entry.pagePath!)}
                          className="accent-primary shrink-0"
                        />
                        <span className="text-base-content">
                          {t(entry.labelKey, { ns: 'common' })}
                        </span>
                      </label>
                    );
                  }
                  // Group
                  const children = entry.children ?? [];
                  const checkedCount = children.filter(
                    (c) => pagePerms[c.pagePath] ?? false,
                  ).length;
                  const allChecked = checkedCount === children.length;
                  const someChecked = checkedCount > 0 && !allChecked;

                  const toggleGroup = () => {
                    const newVal = !allChecked;
                    setPagePerms((prev) => {
                      const next = { ...prev };
                      for (const c of children) {
                        next[c.pagePath] = newVal;
                      }
                      return next;
                    });
                  };

                  return (
                    <div
                      key={entry.labelKey}
                      className="rounded-lg bg-base-200 border border-base-300 overflow-hidden"
                    >
                      <label className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-base-300 transition text-sm font-medium">
                        <IndeterminateCheckbox
                          checked={allChecked}
                          indeterminate={someChecked}
                          onChange={toggleGroup}
                        />
                        <span className="text-base-content">
                          {t(entry.labelKey, { ns: 'common' })}
                        </span>
                        <span className="text-xs text-base-content/40 ml-auto">
                          {checkedCount}/{children.length}
                        </span>
                      </label>
                      <div className="pl-6 pr-3 pb-2 space-y-0.5">
                        {children.map((child) => (
                          <label
                            key={child.pagePath}
                            className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-base-300 transition text-sm"
                          >
                            <input
                              type="checkbox"
                              checked={pagePerms[child.pagePath] ?? false}
                              onChange={() => togglePagePerm(child.pagePath)}
                              className="accent-primary shrink-0"
                            />
                            <span className="text-base-content/80">
                              {t(child.labelKey, { ns: 'common' })}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 操作權限（分組顯示） */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-base-content">
                  {t('actionPermissions')}
                </h3>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setActionPerms(Object.fromEntries(ALL_ACTION_KEYS.map((k) => [k, true])))
                    }
                    className="text-xs text-primary hover:underline"
                  >
                    {t('selectAll')}
                  </button>
                  <span className="text-base-content/30 text-xs">|</span>
                  <button
                    type="button"
                    onClick={() =>
                      setActionPerms(Object.fromEntries(ALL_ACTION_KEYS.map((k) => [k, false])))
                    }
                    className="text-xs text-base-content/50 hover:underline"
                  >
                    {t('clearAll')}
                  </button>
                </div>
              </div>
              <div className="space-y-1">
                {ROLE_ACTION_GROUPS.map((group) => {
                  const checkedCount = group.actions.filter((a) => actionPerms[a] ?? false).length;
                  const allChecked = checkedCount === group.actions.length;
                  const someChecked = checkedCount > 0 && !allChecked;

                  const toggleGroup = () => {
                    const newVal = !allChecked;
                    setActionPerms((prev) => {
                      const next = { ...prev };
                      for (const a of group.actions) {
                        next[a] = newVal;
                      }
                      return next;
                    });
                  };

                  return (
                    <div
                      key={group.labelKey}
                      className="rounded-lg bg-base-200 border border-base-300 overflow-hidden"
                    >
                      <label className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-base-300 transition text-sm font-medium">
                        <IndeterminateCheckbox
                          checked={allChecked}
                          indeterminate={someChecked}
                          onChange={toggleGroup}
                        />
                        <span className="text-base-content">{t(group.labelKey)}</span>
                        <span className="text-xs text-base-content/40 ml-auto">
                          {checkedCount}/{group.actions.length}
                        </span>
                      </label>
                      <div className="pl-6 pr-3 pb-2 space-y-0.5">
                        {group.actions.map((actionKey) => (
                          <label
                            key={actionKey}
                            className="flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-base-300 transition text-sm"
                          >
                            <input
                              type="checkbox"
                              checked={actionPerms[actionKey] ?? false}
                              onChange={() => toggleActionPerm(actionKey)}
                              className="accent-primary shrink-0"
                            />
                            <span className="text-base-content/80">
                              {t(ACTION_KEY_I18N[actionKey] ?? actionKey)}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-2 pt-2 sticky bottom-0 bg-base-100 pb-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 rounded-lg bg-base-300 text-base-content/70 hover:bg-base-200 transition text-sm"
              >
                {t('cancelBtn')}
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 px-4 py-2 rounded-lg bg-primary text-primary-content hover:opacity-90 transition text-sm font-semibold disabled:opacity-50"
              >
                {isLoading ? t('saving') : isEdit ? t('saveBtn') : t('createBtn')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Main Page
// ============================================================

type ModalState = { type: 'none' } | { type: 'create' } | { type: 'edit'; role: RoleDto };

function AdminRolesPageContent({ revision }: { revision: number }) {
  const { t } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();
  const enabledFeatures = useEditionStore((state) => state.enabledFeatures);
  const hasFeature = (key: FeatureKey) => enabledFeatures.includes(key);
  const isSystemUser = useAuthStore((state) => state.user?.isSystemUser);
  const featureFilter = isSystemUser ? undefined : hasFeature;
  const configurablePaths = new Set<string>(
    getConfigurablePages(featureFilter).map((page) => page.pagePath),
  );

  const [modal, setModal] = useState<ModalState>({ type: 'none' });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const msgTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (msgTimer.current) clearTimeout(msgTimer.current);
    },
    [],
  );

  const showMessage = (msg: string, isError = false) => {
    if (msgTimer.current) clearTimeout(msgTimer.current);
    if (isError) {
      setErrorMessage(msg);
      msgTimer.current = setTimeout(() => setErrorMessage(''), 4000);
    } else {
      setSuccessMessage(msg);
      msgTimer.current = setTimeout(() => setSuccessMessage(''), 4000);
    }
  };

  // 查詢角色清單
  const { data: rolesData, isLoading } = useQuery(
    adminVenueListOptions<RoleDto>('roles', revision),
  );

  const roles = rolesData ?? [];

  // 建立角色
  const createMutation = useMutation({
    mutationFn: (data: CreateRoleDto) => apiClient.post<ApiResponse<RoleDto>>('/roles', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setModal({ type: 'none' });
      showMessage(t('roleCreated'));
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('createFailed');
      showMessage(msg, true);
    },
  });

  // 更新角色
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRoleDto }) =>
      apiClient.patch<ApiResponse<RoleDto>>(`/roles/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setModal({ type: 'none' });
      showMessage(t('roleUpdated'));
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('updateFailed');
      showMessage(msg, true);
    },
  });

  return (
    <div className="min-h-screen bg-base-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="text-base-content/40 hover:text-base-content transition text-sm"
            >
              {t('dashboard')}
            </Link>
            <span className="text-base-content/30">/</span>
            <h1 className="heading-font text-xl font-bold text-base-content">
              {t('roleManagement')}
            </h1>
          </div>
          <button
            onClick={() => setModal({ type: 'create' })}
            className="px-4 py-2 rounded-lg bg-primary text-primary-content text-sm font-semibold hover:opacity-90 transition"
          >
            + {t('createRole')}
          </button>
        </div>

        {/* Toast messages */}
        {successMessage && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-sm">
            {successMessage}
          </div>
        )}
        {errorMessage && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-500/15 border border-red-500/30 text-red-400 text-sm">
            {errorMessage}
          </div>
        )}

        {/* Roles Grid */}
        {isLoading ? (
          <div className="p-12 text-center text-base-content/40 text-sm">{t('loading')}</div>
        ) : roles.length === 0 ? (
          <div className="p-12 text-center text-base-content/40 text-sm bg-base-200 rounded-2xl border border-base-300">
            {t('noRoles')}
          </div>
        ) : (
          <div className="grid gap-4">
            {roles.map((role) => {
              const { visible: visiblePages, total: totalPages } = countPagePermissions(
                role.pagePermissions,
                featureFilter,
              );
              const allowedActions = role.actionPermissions.filter((a) => a.isAllowed).length;
              const totalActions = ALL_ACTION_KEYS.length;

              return (
                <div
                  key={role.id}
                  className="bg-base-200 rounded-2xl border border-base-300 p-5 hover:border-primary/40 transition"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h2 className="text-base font-semibold text-base-content">{role.name}</h2>
                        {role.isSystemRole && (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-base-content/10 text-base-content/50 border border-base-content/20">
                            {t('systemBuiltin')}
                          </span>
                        )}
                      </div>
                      {role.description && (
                        <p className="text-sm text-base-content/50 mb-3">{role.description}</p>
                      )}
                      {/* 權限摘要 */}
                      <div className="flex gap-4">
                        <div
                          className="relative text-xs text-base-content/50"
                          title={t('pageCatalogScope')}
                        >
                          {t('pageVisibilitySummary')}
                          <span className="sr-only">{t('pageCatalogScope')}</span>
                          <span className="text-base-content font-medium">{visiblePages}</span>
                          <span className="text-base-content/30">/{totalPages}</span>
                        </div>
                        <div className="text-xs text-base-content/50">
                          {t('actionPermissionsSummary')}
                          <span className="text-base-content font-medium">{allowedActions}</span>
                          <span className="text-base-content/30">/{totalActions}</span>
                        </div>
                      </div>

                      {/* 已授權頁面 badges */}
                      {visiblePages > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {orderPagePermissions(role.pagePermissions)
                            .filter((p) => p.isVisible && configurablePaths.has(p.pagePath))
                            .slice(0, 6)
                            .map((p) => (
                              <span
                                key={p.pagePath}
                                className="px-2 py-0.5 rounded-full text-xs bg-primary/10 text-primary border border-primary/20"
                              >
                                {PAGE_LABEL_KEY_MAP[p.pagePath]
                                  ? t(PAGE_LABEL_KEY_MAP[p.pagePath]!, { ns: 'common' })
                                  : p.pagePath}
                              </span>
                            ))}
                          {visiblePages > 6 && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-base-300 text-base-content/50">
                              {t('plusMore', { count: visiblePages - 6 })}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 ml-4 shrink-0">
                      <button
                        onClick={() => setModal({ type: 'edit', role })}
                        className="px-3 py-1.5 rounded-lg bg-base-300 text-base-content/70 hover:bg-primary/15 hover:text-primary transition text-xs"
                      >
                        {t('editBtn')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!isLoading && (
          <p className="text-xs text-base-content/40 mt-2">
            {t('totalRoles', { count: roles.length })}
          </p>
        )}
      </div>

      {/* Modals */}
      {modal.type === 'create' && (
        <RoleModal
          onClose={() => setModal({ type: 'none' })}
          onSubmit={(data) => createMutation.mutate(data as CreateRoleDto)}
          isLoading={createMutation.isPending}
        />
      )}
      {modal.type === 'edit' && (
        <RoleModal
          role={modal.role}
          onClose={() => setModal({ type: 'none' })}
          onSubmit={(data) => updateMutation.mutate({ id: modal.role.id, data })}
          isLoading={updateMutation.isPending}
        />
      )}
    </div>
  );
}

function AdminRolesPage() {
  const revision = useAuthStore((state) => state.sessionRevision);
  return <AdminRolesPageContent key={revision} revision={revision} />;
}

export default AdminRolesPage;
